import { useCallback, useMemo } from 'react';
import { Edge, Node } from '@xyflow/react';

import { NodeValidationResult, NodeValidationService } from '../services/NodeValidationService';

import { useWorkflow } from './useWorkflow';

/**
 * Hook for validating workflow nodes
 */
export function useNodeValidation() {
  const { getNodeConfig } = useWorkflow();
  const validationService = useMemo(() => {
    const service = NodeValidationService.getInstance();
    service.setNodeConfigGetter(getNodeConfig);
    return service;
  }, [getNodeConfig]);

  /**
   * Validate all nodes in the workflow
   */
  const validateAllNodes = useCallback(
    (nodes: Node[]): NodeValidationResult => {
      return validationService.validateNodes(nodes);
    },
    [validationService]
  );

  /**
   * Validate a single node
   */
  const validateNode = useCallback(
    (node: Node): NodeValidationResult => {
      return validationService.validateNode(node);
    },
    [validationService]
  );

  /**
   * Check if a node has validation errors
   */
  const hasValidationErrors = useCallback(
    (nodeId: string): boolean => {
      return validationService.hasValidationErrors(nodeId);
    },
    [validationService]
  );

  /**
   * Get validation errors for a specific node
   */
  const getNodeValidationErrors = useCallback(
    (nodeId: string) => {
      return validationService.getNodeValidationState(nodeId);
    },
    [validationService]
  );

  /**
   * Get all nodes with validation errors
   */
  const getNodesWithErrors = useCallback((): string[] => {
    return validationService.getNodesWithErrors();
  }, [validationService]);

  /**
   * Clear validation errors for a specific node
   */
  const clearNodeValidation = useCallback(
    (nodeId: string): void => {
      validationService.clearNodeValidation(nodeId);
    },
    [validationService]
  );

  /**
   * Clear validation errors for multiple nodes
   */
  const clearNodesValidation = useCallback(
    (nodeIds: string[]): void => {
      validationService.clearNodesValidation(nodeIds);
    },
    [validationService]
  );

  /**
   * Clear all validation errors
   */
  const clearAllValidation = useCallback((): void => {
    validationService.clearAllValidation();
  }, [validationService]);

  /**
   * Validate node connectivity
   */
  const validateNodeConnectivity = useCallback(
    (nodes: Node[], edges: Edge[]): NodeValidationResult => {
      return validationService.validateNodeConnectivity(nodes, edges);
    },
    [validationService]
  );

  /**
   * Validate both node parameters and connectivity
   */
  const validateNodesWithConnectivity = useCallback(
    (nodes: Node[], edges: Edge[]): NodeValidationResult => {
      return validationService.validateNodesWithConnectivity(nodes, edges);
    },
    [validationService]
  );

  return {
    validateAllNodes,
    validateNode,
    hasValidationErrors,
    getNodeValidationErrors,
    getNodesWithErrors,
    clearNodeValidation,
    clearNodesValidation,
    clearAllValidation,
    validateNodeConnectivity,
    validateNodesWithConnectivity,
  };
}
